'use client'

import { useMemo } from 'react'
import { useParams } from 'next/navigation'
import { Sorter } from '@/components/Products/Series/SeriesProductsList/SeriesProductsCard/SeriesProductsCardItemsList/SeriesProductsCardItemsList'
import {
	Button,
	ClippedImage,
	Copy,
	RightTopArrow,
	Tag,
	cn,
} from '@desoutter/uikit'
import { Specification } from '@/app/api/types/redpim.types'
import { LocalizedLink } from '@/components/LocalizedLink'

type ProductListItemProps = {
	partnumber: string
	model: string
	specifications?: Specification[]
	sorters?: Sorter[]
	imageUrl?: string
	className?: string
}

export default function ProductListItem({
	partnumber,
	model,
	specifications,
	sorters,
	imageUrl,
	className,
}: ProductListItemProps) {
	const { lang } = useParams()
	const formatSpecifications: { attributeId: string; value: string }[] =
		useMemo(() => {
			if (!sorters) return []

			if (!specifications) {
				return sorters.slice(2).map((sorter) => ({
					attributeId: sorter.value,
					value: '-',
				}))
			}

			return sorters.reduce((acc, sorter) => {
				if (sorter.value === 'model' || sorter.value === 'partnumber') {
					return acc
				}
				const spec = specifications.find(
					(spec) => spec.attributeId === sorter.value,
				)
				if (spec) {
					acc.push({
						attributeId: spec.attributeId,
						value: spec.value
							? `${spec.value}${spec?.unit ? ' ' + spec.unit : ''}`
							: '-',
					})
				} else {
					acc.push({
						attributeId: sorter.value,
						value: '-',
					})
				}

				return acc.sort((a, b) => {
					let indexA = sorters.findIndex(
						(sorter) => sorter.value === a.attributeId,
					)
					let indexB = sorters.findIndex(
						(sorter) => sorter.value === b.attributeId,
					)
					return indexA - indexB
				})
			}, [])
		}, [specifications, sorters])

	return (
		<LocalizedLink
			locale={lang}
			href={`/products/${partnumber}`}
			className="w-full"
			prefetch={false}
			rel="nofollow"
		>
			<div
				className={cn(
					'bg-gray-50 rounded-3xl flex md:h-20 min-w-full w-max gap-6 px-4 py-3 justify-between',
					className,
				)}
			>
				<div className="flex gap-4 md:gap-6 flex-1">
					<div className="flex items-center w-14 stretch-0">
						<ClippedImage
							src={imageUrl}
							alt="product"
							width={56}
							height={56}
							sourceWidth={400}
							sourceHeight={400}
						/>
					</div>

					<div className="flex flex-1 gap-8">
						<div className="flex items-center">
							<div className="h5 font-bold m-0 ml-auto md:w-64 text-md line-clamp-1">
								{model}
							</div>
						</div>
						<div className="flex items-center ">
							<div
								className="bg-foreground text-background rounded flex items-center justify-center gap-1 px-1"
								onClick={(e) => {
									e.preventDefault()
									e.stopPropagation()
									navigator.clipboard.writeText(partnumber)
								}}
							>
								<Tag
									variant="tertiary"
									label={partnumber}
									className="w-fit text-center px-0"
								/>

								<div className="bg-foreground text-background flex items-center">
									<Copy xs />
								</div>
							</div>
						</div>
						{formatSpecifications.map((sorter, index) => (
							<div
								key={index}
								className="flex items-center justify-center w-36"
							>
								<span className="text-center line-clamp-2">{sorter.value}</span>
							</div>
						))}
					</div>
				</div>
				<div className="flex items-center">
					<Button
						title="go to article"
						color="secondary"
						icon={RightTopArrow}
						size="md"
					/>
				</div>
			</div>
		</LocalizedLink>
	)
}
